import { busService } from "../bus";
import { getQueryParameter, LayoutUpdatedMessage, MulticastDelegate } from "flavr-common";
import { Matrix4, Mesh, MeshBasicMaterial, Quaternion, Vector3 } from "three";
import { Pathfinding } from "three-pathfinding";
import { mainScene } from "../rendering/mainScene";
import { MeshRenderable } from "../rendering/mesh/MeshRenderable";
import { isRenderableType } from "../rendering/renderable/Renderable";
import { roomService } from "../room/RoomService";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { modelPool } from "@modules/model";
import { clampNavMeshStep } from "./clampNavMeshStep";
const DEBUG = !!getQueryParameter(window.location.href, "debug");
const tmpPosition = new Vector3();
const tmpQuaternion = new Quaternion();
const tmpScale = new Vector3();
const tmpRaycastMesh = new Mesh();
class NavMeshService {
    constructor() {
        this.pathfinding = new Pathfinding();
        this.layers = new Map();
        this.dirtyLayers = new Set();
        this.onShouldRecomputeDelegate = new MulticastDelegate();
        // ToDo: count enables
        this.enabled = true;
        this.connection = busService.connection("PathfinderService");
        this._currentLocalLayerId = 0;
        this._currentLayerId = this.generateLayerId(roomService.currentRoomModelUID, this._currentLocalLayerId);
        this.onRoomChanged = () => {
            this.changeCurrentLayerId(0);
            this.buildNavMesh(this.currentLayerId);
            this.onShouldRecomputeDelegate.invoke();
        };
        roomService.onRoomChangedDelegate.add(this.onRoomChanged);
        this.connection.subscribeByType(LayoutUpdatedMessage, () => {
            this.clear();
        });
    }
    get currentLayerId() {
        return this._currentLayerId;
    }
    generateLayerId(modelUID, localLayerId) {
        const modelName = modelPool.getModelOrThrow(modelUID).getName();
        return `${modelName}_${localLayerId}`;
    }
    clear() {
        if (this.rebuildRequestId !== undefined) {
            cancelAnimationFrame(this.rebuildRequestId);
            this.rebuildRequestId = undefined;
        }
        this.dirtyLayers.clear();
        for (const layer of this.layers.values()) {
            for (const data of layer.navMeshes.values()) {
                if (data.geometry) {
                    data.geometry.dispose();
                    data.geometry = undefined;
                }
                this.removeHelperMesh(data);
            }
        }
        this.layers.clear();
        this.pathfinding.zones = {};
        this._currentLayerId = this.generateLayerId(roomService.currentRoomModelUID, 0);
        this.onShouldRecomputeDelegate.invoke();
    }
    changeCurrentLayerId(layerId) {
        const newLayerId = this.generateLayerId(roomService.currentRoomModelUID, layerId);
        if (newLayerId === this._currentLayerId)
            return;
        this._currentLayerId = newLayerId;
        this.onShouldRecomputeDelegate.invoke();
    }
    requestBuildNavMesh(layerId) {
        this.dirtyLayers.add(layerId);
        if (this.rebuildRequestId !== undefined)
            return;
        this.rebuildRequestId = requestAnimationFrame(() => {
            this.rebuildRequestId = undefined;
            const dirtyLayers = Array.from(this.dirtyLayers);
            this.dirtyLayers.clear();
            for (const dirtyLayerId of dirtyLayers) {
                this.buildNavMesh(dirtyLayerId);
            }
        });
    }
    addNavMesh(navMesh) {
        if (navMesh.layerId === undefined)
            return;
        let layer = this.layers.get(navMesh.layerId);
        if (layer === undefined) {
            layer = {
                navMeshes: new Map(),
                version: 0
            };
            this.layers.set(navMesh.layerId, layer);
        }
        layer.navMeshes.set(navMesh, {
            enabled: true,
            matrix: new Matrix4()
        });
    }
    removeNavMesh(navMesh) {
        if (navMesh.layerId === undefined)
            return;
        const layer = this.layers.get(navMesh.layerId);
        if (!layer) {
            return;
        }
        const data = layer.navMeshes.get(navMesh);
        if (data) {
            if (data.geometry) {
                data.geometry.dispose();
                data.geometry = undefined;
            }
            this.removeHelperMesh(data);
        }
        layer.navMeshes.delete(navMesh);
    }
    updateNavMesh(navMesh) {
        if (navMesh.layerId === undefined)
            return;
        const layer = this.layers.get(navMesh.layerId);
        if (!layer) {
            return;
        }
        const data = layer.navMeshes.get(navMesh);
        if (!data)
            return;
        if (data.helperMesh) {
            data.matrix.decompose(tmpPosition, tmpQuaternion, tmpScale);
            data.helperMesh.position.copy(navMesh.owner.worldPosition).sub(tmpPosition);
            data.helperMesh.quaternion.copy(navMesh.owner.worldOrientation).multiply(tmpQuaternion.invert());
            data.helperMesh.scale.copy(navMesh.owner.worldScale).divide(tmpScale);
        }
    }
    buildNavMesh(layerId) {
        var _a;
        const layer = this.layers.get(layerId);
        if (!layer)
            return;
        const layerName = `${layerId}`;
        delete this.pathfinding.zones[layerName];
        for (const data of layer.navMeshes.values()) {
            this.removeHelperMesh(data);
        }
        const geometries = [];
        for (const [navMesh, data] of layer.navMeshes) {
            if (!navMesh.enabled)
                continue;
            if (!data.enabled)
                continue;
            const renderable = navMesh.owner.renderable;
            if (!renderable)
                continue;
            if (!isRenderableType(MeshRenderable, renderable))
                continue;
            if (!renderable.meshInstance.isLoaded)
                continue;
            const geometryData = renderable.meshInstance.getGeometry();
            if (!geometryData)
                continue;
            (_a = data.geometry) === null || _a === void 0 ? void 0 : _a.dispose();
            data.zone = undefined;
            data.geometry = geometryData.geometry.clone();
            for (const key in data.geometry.attributes) {
                if (key !== "position") {
                    data.geometry.deleteAttribute(key);
                }
            }
            data.matrix.copy(navMesh.owner.matrixWorld);
            data.geometry.applyMatrix4(data.matrix);
            if (DEBUG) {
                data.helperMesh = new Mesh(data.geometry, new MeshBasicMaterial({
                    wireframe: true,
                    transparent: true,
                    depthTest: false,
                    depthWrite: false
                }));
                mainScene.add(data.helperMesh);
            }
            geometries.push(data);
        }
        if (geometries.length === 0)
            return;
        layer.version++;
        if (geometries.length === 1) {
            const data = geometries[0];
            if (!data.zone) {
                data.zone = Pathfinding.createZone(data.geometry);
            }
            navMeshService.pathfinding.setZoneData(layerName, data.zone);
        }
        else {
            const toMergeGeometries = [];
            for (const data of geometries) {
                if (data.geometry) {
                    toMergeGeometries.push(data.geometry);
                }
            }
            if (toMergeGeometries.length === 0)
                return;
            const mergedGeometry = BufferGeometryUtils.mergeGeometries(toMergeGeometries, false);
            let needsRecreateZone = false;
            for (let i = 0; i < geometries.length - 1; i++) {
                if (geometries[i].zone === undefined) {
                    needsRecreateZone = true;
                    break;
                }
                if (geometries[i].zone !== geometries[i + 1].zone) {
                    needsRecreateZone = true;
                    break;
                }
            }
            const data = geometries[0];
            let zone = data.zone;
            if (!zone || needsRecreateZone) {
                zone = Pathfinding.createZone(mergedGeometry);
                for (const data of geometries) {
                    data.zone = zone;
                }
            }
            navMeshService.pathfinding.setZoneData(layerName, zone);
        }
    }
    hasLayer(layerId) {
        return layerId in this.pathfinding.zones;
    }
    createNodeGroup(layerId, start) {
        const layerName = `${layerId}`;
        const node = {
            layerId,
            layerName,
            groupId: undefined,
            navNode: undefined,
            version: 0
        };
        if (!(layerName in this.pathfinding.zones)) {
            return node;
        }
        const layer = this.layers.get(layerId);
        if (!layer) {
            return node;
        }
        node.version = layer.version;
        node.groupId = this.pathfinding.getGroup(layerName, start, true);
        if (node.groupId === null) {
            node.groupId = this.pathfinding.getGroup(layerName, start, false);
        }
        node.navNode =
            node.groupId != null
                ? this.pathfinding.getClosestNode(start, layerName, node.groupId, true) || this.pathfinding.getClosestNode(start, layerName, node.groupId)
                : null;
        return node;
    }
    findPositionOnNavMesh(nodeGroup, start, end, outPos) {
        if (!this.enabled) {
            outPos.copy(end);
            return false;
        }
        if (!(nodeGroup.layerName in this.pathfinding.zones)) {
            outPos.copy(end);
            return false;
        }
        const layer = this.layers.get(nodeGroup.layerId);
        if (!layer) {
            outPos.copy(end);
            return false;
        }
        if (nodeGroup.version !== layer.version) {
            nodeGroup.groupId = this.pathfinding.getGroup(nodeGroup.layerName, start, true);
            nodeGroup.navNode =
                nodeGroup.groupId != null
                    ? this.pathfinding.getClosestNode(start, nodeGroup.layerName, nodeGroup.groupId, true) ||
                        this.pathfinding.getClosestNode(start, nodeGroup.layerName, nodeGroup.groupId)
                    : null;
            nodeGroup.version = layer.version;
        }
        if (nodeGroup.groupId === null || nodeGroup.groupId === undefined) {
            nodeGroup.groupId = this.pathfinding.getGroup(nodeGroup.layerName, end, true);
        }
        if (nodeGroup.groupId === undefined || nodeGroup.groupId === null) {
            outPos.copy(end);
            return false;
        }
        if (nodeGroup.navNode === null || nodeGroup.navNode === undefined) {
            nodeGroup.navNode =
                this.pathfinding.getClosestNode(start, nodeGroup.layerName, nodeGroup.groupId, true) ||
                    this.pathfinding.getClosestNode(start, nodeGroup.layerName, nodeGroup.groupId);
        }
        if (!nodeGroup.navNode) {
            outPos.copy(end);
            return false;
        }
        nodeGroup.navNode = this.clampStep(start, end, nodeGroup.navNode, nodeGroup.layerName, nodeGroup.groupId, outPos);
        return true;
    }
    removeHelperMesh(data) {
        if (!data.helperMesh)
            return;
        if (data.helperMesh.material && !Array.isArray(data.helperMesh.material)) {
            data.helperMesh.material.dispose();
        }
        if (data.helperMesh.geometry) {
            data.helperMesh.geometry.dispose();
        }
        data.helperMesh.removeFromParent();
        data.helperMesh = undefined;
    }
    clampStep(startRef, endRef, node, zoneID, groupID, endTarget) {
        const vertices = this.pathfinding.zones[zoneID].vertices;
        const nodes = this.pathfinding.zones[zoneID].groups[groupID];
        return clampNavMeshStep(startRef, endRef, node, vertices, nodes, endTarget);
    }
    raycast(raycaster, layerId) {
        const intersections = [];
        const layer = this.layers.get(layerId);
        if (!layer) {
            return intersections;
        }
        for (const [navMesh, data] of layer.navMeshes) {
            if (!navMesh.enabled)
                continue;
            if (!data.geometry)
                continue;
            tmpRaycastMesh.geometry = data.geometry;
            tmpRaycastMesh.raycast(raycaster, intersections);
        }
        return intersections;
    }
}
export const navMeshService = new NavMeshService();
